import React, { useState, useEffect } from "react";
import "./AnnouncementSelectionTemplate.scss";
import RoundTrueIcon from "../../../../components/icons/RoundTrueIcon";
import AnnouncementSelectTemplate from "./AnnouncementTemplateSelect/AnnouncementSelectTemplate";
import toast from "react-hot-toast";
import { TemplatesApi } from "../../../../utils/api/Templates";
import PreviewModal from "../../workspace/propertypost/view/PreviewModal";
import Loading from "../../../loading/Loading";

const getApiBaseUrl = () => {
  return process.env.REACT_APP_API_ENDPOINT || "http://localhost:5000";
};

const transformApiDataToVersions = (apiData) => {
  if (!apiData) return [];

  const allConfigs = Object.values(apiData).flatMap(
    (postType) =>
      postType?.templates?.flatMap((template) => template.config || []) || []
  );

  if (allConfigs.length === 0) {
    return [];
  }

  const groupedByName = allConfigs.reduce((acc, config) => {
    const name = config.design_template?.name;
    if (name) {
      if (!acc[name]) {
        acc[name] = [];
      }
      acc[name].push(config);
    }
    return acc;
  }, {});

  const formattedVersions = Object.entries(groupedByName)
    .map(([versionName, configs]) => {
      const designTemplate = configs[0]?.design_template;
      if (!designTemplate) return null;

      const baseUrl = getApiBaseUrl();

      const isSelected = (userModel, postType) => {
        const config = configs.find(
          (c) =>
            c.user_ref_model === userModel && c.post_type.startsWith(postType)
        );
        return config?.status === "active";
      };

      const getImageUrl = (previewRef) => {
        if (previewRef?.compressed_file_path) {
          return `${baseUrl}/${previewRef.compressed_file_path}`;
        }
        return "/images/default-placeholder.png";
      };

      const templates = [
        {
          id: `${designTemplate._id}-agency-image`,
          type: "Agency Image Post",
          img: getImageUrl(designTemplate.agency_image_preview_file_ref),
          isSelected: isSelected("agencies", "image"),
          configId: configs.find(
            (c) =>
              c.user_ref_model === "agencies" && c.post_type.startsWith("image")
          )?._id,
        },
        {
          id: `${designTemplate._id}-agency-story`,
          type: "Agency Story Post",
          img: getImageUrl(designTemplate.agency_story_preview_file_ref),
          isSelected: isSelected("agencies", "story"),
          configId: configs.find(
            (c) =>
              c.user_ref_model === "agencies" && c.post_type.startsWith("story")
          )?._id,
        },
        {
          id: `${designTemplate._id}-agent-image`,
          type: "Agent Image Post",
          img: getImageUrl(designTemplate.agent_image_preview_file_ref),
          isSelected: isSelected("agents", "image"),
          configId: configs.find(
            (c) =>
              c.user_ref_model === "agents" && c.post_type.startsWith("image")
          )?._id,
        },
        {
          id: `${designTemplate._id}-agent-story`,
          type: "Agent Story Post",
          img: getImageUrl(designTemplate.agent_story_preview_file_ref),
          isSelected: isSelected("agents", "story"),
          configId: configs.find(
            (c) =>
              c.user_ref_model === "agents" && c.post_type.startsWith("story")
          )?._id,
        },
      ];

      return {
        version: versionName,
        templates: templates,
      };
    })
    .filter(Boolean);

  return formattedVersions;
};

const AnnouncementSelectionTemplate = ({
  isOpen,
  onClose,
  announcementType,
}) => {
  const [isSelectTemplateOpen, setIsSelectTemplateOpen] = useState(false);
  const [templateVersions, setTemplateVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);

  const fetchTemplates = async () => {
    if (!announcementType?.slug) return;

    setIsLoading(true);
    try {
      const response = await TemplatesApi.getAgencyAnnouncementTemplates({
        announcement_type: announcementType.slug,
      });

      if (response.success && response.data?.data) {
        const transformedData = transformApiDataToVersions(response.data.data);
        setTemplateVersions(transformedData);
      } else {
        setTemplateVersions([]);
      }
    } catch (error) {
      toast.error("Error while fetching templates.");
      setTemplateVersions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) fetchTemplates();
  }, [isOpen, announcementType]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleModalContentClick = (e) => e.stopPropagation();

  const shouldHideParent = isSelectTemplateOpen;

  const handleTemplateConfirm = async (selectedTemplate) => {
    console.log("Selected template confirmed:", selectedTemplate);
    setIsSelectTemplateOpen(false);

    await fetchTemplates();
  };

  const handlePreviewClick = (imageUrl) => {
    setPreviewImages([imageUrl]);
    setPreviewIndex(0);
    setPreviewOpen(true);
  };

  return (
    <>
      {!shouldHideParent && (
        <div className="annoucement-backdrop">
          <div
            className="annoucement-content"
            onClick={handleModalContentClick}
          >
            <div className="annoucement-header">
              <div className="header-text">
                <h2>{announcementType.label} Design Templates</h2>
                <p>Select design templates</p>
              </div>
              <button
                className="select-template-btn"
                onClick={() => setIsSelectTemplateOpen(true)}
              >
                Select Template
              </button>
            </div>

            <div className="annoucement-body">
              {isLoading ? (
                <div className="templates-list-container center-content">
                  <Loading />
                </div>
              ) : templateVersions.length > 0 ? (
                templateVersions.map((versionData, index) => (
                  <div
                    className="template-version-group"
                    key={versionData.version + index}
                  >
                    <div className="version-header">
                      <h3>{versionData.version}</h3>
                      <button
                        className="remove-btn"
                        onClick={async () => {
                          const allConfigIds = versionData.templates
                            .map((t) => t.configId)
                            .filter(Boolean);

                          if (allConfigIds.length === 0) {
                            toast.error(
                              "No valid configuration IDs found for this template group."
                            );
                            return;
                          }

                          setIsLoading(true);
                          try {
                            await Promise.all(
                              allConfigIds.map((id) =>
                                TemplatesApi.deleteTemplateConfig(id)
                              )
                            );

                            toast.success("Template removed successfully!");

                            const response =
                              await TemplatesApi.getAgencyAnnouncementTemplates(
                                {
                                  announcement_type: announcementType.slug,
                                }
                              );

                            if (response.success && response.data?.data) {
                              const transformedData =
                                transformApiDataToVersions(response.data.data);
                              setTemplateVersions(transformedData);
                            } else {
                              setTemplateVersions([]);
                            }
                          } catch (error) {
                            console.error("Error removing template:", error);
                            toast.error(
                              "An error occurred while removing template(s)."
                            );
                          } finally {
                            setIsLoading(false);
                          }
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    <div className="template-list">
                      {versionData.templates.map((template) => (
                        <div
                          className={`template-card ${
                            template.type.includes("Story") ? "story-card" : ""
                          }`}
                          key={template.id}
                        >
                          <div className="annoucement-image-wrapper">
                            <img
                              src={template.img}
                              alt={template.type}
                              onClick={() => handlePreviewClick(template.img)}
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                          <div className="selected-badge">
                            <RoundTrueIcon />
                            <span>Selected</span>
                          </div>
                          <div className="template-info">
                            <p>{template.type}</p>
                            <button
                              type="button"
                              className="preview-link"
                              onClick={() => handlePreviewClick(template.img)}
                            >
                              Preview
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state-design">
                  <p>No design templates have been selected.</p>
                  <p>
                    Please click the 'Select Template' button to choose your
                    templates.
                  </p>
                  <div className="note-section">
                    <p className="note-title">NOTE</p>
                    <p>
                      You can select multiple templates for the announcement
                      post.
                    </p>
                  </div>
                  <button
                    className="select-template-btn"
                    onClick={() => setIsSelectTemplateOpen(true)}
                  >
                    Select Templates
                  </button>
                </div>
              )}
            </div>

            <div className="announcement-footer">
              <button className="cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button className="done-btn" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <AnnouncementSelectTemplate
        isOpen={isSelectTemplateOpen}
        onClose={() => setIsSelectTemplateOpen(false)}
        onConfirm={handleTemplateConfirm}
        announcementType={announcementType}
        templateTypeIdProp="announcement_for_sale_image_post"
        existingTemplates={templateVersions.flatMap(
          (v) => v.templates.map((t) => t.id.split("-")[0]) // design_template._id part
        )}
      />

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        images={previewImages}
        initialIndex={previewIndex}
      />
    </>
  );
};

export default AnnouncementSelectionTemplate;
